import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { sessionsApi } from "@/lib/api";
import { useStateCtx } from "@/context/StateContext";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import { Plus, Lock, Clock, Bomb, Users, ArrowRight, X, Loader2 } from "lucide-react";

export default function Sessions() {
  const { stateCode } = useStateCtx();
  const { user } = useAuth();
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);

  const refresh = async () => {
    try {
      const r = await sessionsApi.list(stateCode === "ALL" ? null : stateCode);
      setList(r);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); const i = setInterval(refresh, 20000); return () => clearInterval(i); }, [stateCode]);

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="blob" style={{ width: 380, height: 380, background: "#FF1A8C", top: -120, left: -100 }} />
      <div className="blob" style={{ width: 320, height: 320, background: "#15FF73", bottom: -120, right: -80, animationDelay: "-5s" }} />
      <NavBar />
      <Toaster position="top-right" theme="light" richColors />

      <div className="max-w-5xl mx-auto px-5 lg:px-10 py-12 relative">
        <div className="flex items-end justify-between mb-7 gap-4 flex-wrap">
          <div>
            <span className="sticker sticker-magenta mb-3 inline-block"><Lock size={11} strokeWidth={2.4} /> Encrypted · Ephemeral</span>
            <h1 className="font-display text-5xl sm:text-6xl font-extrabold leading-[0.9] tracking-tight mt-3">
              Live <span className="text-rainbow">sessions.</span>
            </h1>
            <p className="text-sm opacity-65 mt-3 max-w-xl">Connoisseurs trading notes. Every room runs on its own ephemeral encryption key. Detonates on close. <span className="font-bold">Tips appreciated.</span></p>
          </div>
          <button
            data-testid="session-host"
            onClick={() => user ? setComposing(true) : nav("/auth?mode=login")}
            className="btn-x btn-primary"
          >
            <Plus size={14} strokeWidth={2.6} /> Host a session
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16 opacity-60"><Loader2 size={28} className="mx-auto animate-spin" /></div>
        ) : list.length === 0 ? (
          <div className="card-x card-cyan bg-[#FAF6E8] p-12 text-center">
            <div className="text-7xl mb-3 bouncy">💨</div>
            <h2 className="font-display text-3xl font-extrabold mb-1">No sessions yet.</h2>
            <p className="text-sm opacity-70 mb-5">Be the first host. Open a room. Connoisseurs will roll in.</p>
            <button onClick={() => user ? setComposing(true) : nav("/auth?mode=login")} className="btn-x btn-primary">Host a session <Plus size={14} /></button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5">
            {list.map((s) => (
              <SessionCard key={s.id} s={s} />
            ))}
          </div>
        )}
      </div>

      {composing && <ComposerModal onClose={() => setComposing(false)} onCreated={(s) => { setComposing(false); nav(`/sessions/${s.id}`); }} />}
      <Footer />
    </div>
  );
}

function SessionCard({ s }) {
  const expires = new Date(s.expires_at);
  const minsLeft = Math.max(0, Math.round((expires - new Date()) / 60000));
  return (
    <Link to={`/sessions/${s.id}`} className="card-x card-cyan bg-[#FAF6E8] p-5 hover:scale-[1.01] transition-transform">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-12 h-12 rounded-full bg-[#15FF73] border-2 border-[#0B0F0C] flex items-center justify-center text-sm font-extrabold flex-shrink-0">
          {(s.host_handle || "?")[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl font-extrabold leading-tight truncate">{s.title}</div>
          <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest opacity-65 truncate">@{s.host_handle} · {s.topic || "open"}</div>
        </div>
        {s.tips_total > 0 && <span className="sticker sticker-lemon !text-[10px]">${Math.round(s.tips_total)} tipped</span>}
      </div>
      <div className="flex flex-wrap gap-2 text-[10px] font-mono-x font-bold uppercase tracking-widest">
        <span className="px-2 py-1 rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8] flex items-center gap-1"><Users size={10} /> {(s.members || []).length}</span>
        <span className="px-2 py-1 rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8] flex items-center gap-1"><Clock size={10} /> TTL {s.ttl_minutes}m</span>
        <span className="px-2 py-1 rounded-full border-2 border-[#0B0F0C] bg-[#FFE74C] flex items-center gap-1"><Bomb size={10} /> {minsLeft}m left</span>
      </div>
    </Link>
  );
}

function ComposerModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [topic, setTopic] = useState("vibes");
  const [ttl, setTtl] = useState(60);
  const [vis, setVis] = useState("public");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Give it a name"); return; }
    setBusy(true);
    try {
      const s = await sessionsApi.create({ title, topic, ttl_minutes: ttl, visibility: vis });
      onCreated?.(s);
    } catch (e) { toast.error("Couldn't host"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#0B0F0C]/85 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="card-x card-magenta bg-[#FAF6E8] p-5 max-w-md w-full">
        <div className="flex items-center mb-3">
          <span className="font-display text-2xl font-extrabold">Host a session</span>
          <button onClick={onClose} className="ml-auto w-9 h-9 rounded-full border-2 border-[#0B0F0C] flex items-center justify-center hover:bg-[#FF1A8C] hover:text-white"><X size={14} strokeWidth={2.4} /></button>
        </div>
        <input
          data-testid="session-title"
          value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Session title (e.g., 'Sunday afternoon, GG4 deep-dive')"
          className="w-full bg-white border-2 border-[#0B0F0C] rounded-full px-4 h-12 text-sm font-medium outline-none focus:shadow-[3px_3px_0_#0B0F0C] mb-3"
        />
        <div className="grid grid-cols-3 gap-2 mb-3">
          {["vibes", "tasting", "review"].map((t) => (
            <button key={t} onClick={() => setTopic(t)} className={`py-2 rounded-full border-2 border-[#0B0F0C] text-[10px] font-mono-x font-bold uppercase ${topic === t ? "bg-[#15FF73]" : "bg-[#FAF6E8]"}`}>{t}</button>
          ))}
        </div>
        <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest opacity-65 mb-2">Auto-detonate after</div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[5, 60, 1440].map((n) => (
            <button key={n} onClick={() => setTtl(n)} className={`py-2 rounded-full border-2 border-[#0B0F0C] text-[10px] font-mono-x font-bold uppercase ${ttl === n ? "bg-[#FFE74C]" : "bg-[#FAF6E8]"}`}>
              {n === 5 ? "5 min" : n === 60 ? "1 hour" : "24 hours"}
            </button>
          ))}
        </div>
        <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest opacity-65 mb-2">Visibility</div>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {[["public", "Public"], ["invite", "Invite only"]].map(([v, l]) => (
            <button key={v} onClick={() => setVis(v)} className={`py-2 rounded-full border-2 border-[#0B0F0C] text-[10px] font-mono-x font-bold uppercase ${vis === v ? "bg-[#FF1A8C] text-white" : "bg-[#FAF6E8]"}`}>{l}</button>
          ))}
        </div>
        <button data-testid="session-create" onClick={submit} disabled={busy} className="btn-x btn-primary w-full justify-center disabled:opacity-50">
          {busy ? <><Loader2 size={14} className="animate-spin" /> Opening room…</> : <>Open the room <ArrowRight size={14} /></>}
        </button>
      </div>
    </div>
  );
}
